"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

interface MaintenanceModeResponse {
	enabled: boolean;
}

export function useMaintenanceMode() {
	return useQuery<MaintenanceModeResponse>({
		queryKey: ["admin", "maintenance"],
		queryFn: async () => {
			const response = await fetch("/api/admin/maintenance");
			if (!response.ok) {
				throw new Error("Failed to fetch maintenance mode");
			}
			return response.json();
		},
		// Only refetch when window is focused, not continuously polling
		refetchOnWindowFocus: true,
		staleTime: 30000, // Consider data fresh for 30 seconds
		retry: 1,
	});
}

export function useToggleMaintenanceMode() {
	const queryClient = useQueryClient();

	return useMutation<
		MaintenanceModeResponse,
		Error,
		boolean,
		{ previousValue: MaintenanceModeResponse | undefined }
	>({
		mutationFn: async (enabled: boolean) => {
			const response = await fetch("/api/admin/maintenance", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({ enabled }),
			});

			if (!response.ok) {
				const error = await response.json();
				throw new Error(error.error || "Failed to toggle maintenance mode");
			}

			return response.json();
		},
		// Optimistic update
		onMutate: async (enabled) => {
			// Cancel outgoing refetches
			await queryClient.cancelQueries({ queryKey: ["admin", "maintenance"] });

			// Snapshot previous value
			const previousValue = queryClient.getQueryData<MaintenanceModeResponse>([
				"admin",
				"maintenance",
			]);

			// Optimistically update
			queryClient.setQueryData<MaintenanceModeResponse>(["admin", "maintenance"], { enabled });

			return { previousValue };
		},
		// On error, rollback
		onError: (err, enabled, context) => {
			if (context?.previousValue) {
				queryClient.setQueryData<MaintenanceModeResponse>(
					["admin", "maintenance"],
					context.previousValue,
				);
			}
		},
		// Always refetch after mutation
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: ["admin", "maintenance"] });
		},
	});
}
